import type { MonthDay } from '@fall26/shared'
import { useState } from 'react'
import { useMonth } from '../api/hooks.js'

/**
 * The month.
 *
 * Three things have to be distinguishable at a glance and they use three
 * different channels, so none of them competes:
 *
 *   course       a coloured dot — identity only, never meaning
 *   exam         a filled chip; labs get an outlined one
 *   day shading  the registrar's own calendar: breaks, study days, finals
 *
 * Saturdays and Sundays are in the grid. December 12th is a Saturday, a finals
 * day, and the date of ME 334's final.
 */
export function Calendar() {
  const [month, setMonth] = useState('current')
  const query = useMonth(month)
  const [selected, setSelected] = useState<MonthDay | null>(null)

  if (query.isError) {
    return (
      <p className="rounded-[var(--radius)] border border-[var(--overdue)]/30 bg-[var(--overdue-wash)] p-4 text-sm text-[var(--overdue)]">
        Could not load the calendar.
      </p>
    )
  }
  if (!query.data) return <div className="h-96 animate-pulse rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]" />

  const data = query.data

  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setMonth(data.prevMonth)} aria-label="Previous month" className={navButton}>
          ←
        </button>
        <h1 className="min-w-0 flex-1 text-center text-[15px] font-semibold tracking-tight text-[var(--text)]">
          {data.label}
        </h1>
        <button type="button" onClick={() => setMonth(data.nextMonth)} aria-label="Next month" className={navButton}>
          →
        </button>
      </div>

      <Legend />

      {/*
        * Seven columns have to divide the width, and 44px each needs 308px of
        * grid. Inside the page gutter a 320px phone leaves 286 — 41px cells.
        * So below 360px the month goes edge to edge and drops its side border,
        * which is what a native calendar does at that width anyway, and the
        * cells come out at 45px. Above it the card sits in the gutter like
        * every other card.
        */}
      <div className="-mx-4 overflow-hidden border-y border-[var(--border)] bg-[var(--surface)] min-[360px]:mx-0 min-[360px]:rounded-[var(--radius-lg)] min-[360px]:border-x">
        <div className="grid grid-cols-7 border-b border-[var(--rule)]">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
            <div
              key={d}
              className="tabular py-1.5 text-center text-[10px] uppercase tracking-[0.1em] text-[var(--text-subtle)]"
            >
              {d}
            </div>
          ))}
        </div>

        {data.weeks.map((week) => (
          <div key={week[0]?.date} className="grid grid-cols-7 border-b border-[var(--rule)] last:border-b-0">
            {week.map((day) => (
              <DayCell key={day.date} day={day} onSelect={() => setSelected(day)} />
            ))}
          </div>
        ))}
      </div>

      {selected && <DayDetail day={selected} onClose={() => setSelected(null)} />}
    </section>
  )
}

const navButton =
  'tap flex w-11 items-center justify-center rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)]'

/** The registrar's shading, resolved to one background. */
function shadeOf(day: MonthDay): string {
  if (day.isFinalExamDay) return 'var(--day-finals)'
  if (day.isStudyDay) return 'var(--day-study)'
  if (day.noClassReason) return 'var(--day-break)'
  return 'transparent'
}

function DayCell({ day, onSelect }: { day: MonthDay; onSelect: () => void }) {
  const exams = day.entries.filter((e) => e.kind === 'exam')
  const others = day.entries.filter((e) => e.kind !== 'exam')

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`min-h-[72px] border-r border-[var(--rule)] p-1 text-left align-top last:border-r-0 md:min-h-[110px] ${
        day.inMonth ? '' : 'opacity-40'
      }`}
      style={{ backgroundColor: shadeOf(day) }}
    >
      <div className="flex items-center justify-between gap-1">
        <span
          className={`tabular inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] ${
            day.isToday
              ? 'bg-[var(--accent)] font-semibold text-[var(--accent-contrast)]'
              : day.isPast
                ? 'text-[var(--text-subtle)]'
                : 'text-[var(--text)]'
          }`}
        >
          {day.dayOfMonth}
        </span>
        {day.labs.length > 0 && (
          <span
            aria-label="lab"
            className="tabular rounded-[3px] border px-1 text-[9px] uppercase tracking-[0.06em]"
            style={{
              borderColor: `var(--course-${day.labs[0]!.colorIndex % 6})`,
              color: `var(--course-${day.labs[0]!.colorIndex % 6})`,
            }}
          >
            lab
          </span>
        )}
      </div>

      <div className="mt-1 space-y-0.5">
        {/* Exams are filled; everything else is a dot and a title. */}
        {exams.map((e) => (
          <div
            key={e.itemId}
            className="truncate rounded-[3px] px-1 text-[10px] font-medium text-[var(--accent-contrast)]"
            style={{ backgroundColor: `var(--course-${e.colorIndex % 6})` }}
          >
            {e.title}
          </div>
        ))}
        {others.slice(0, 3).map((e) => (
          <div key={e.itemId} className="flex items-center gap-1">
            <span
              aria-hidden
              className="h-1.5 w-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: `var(--course-${e.colorIndex % 6})` }}
            />
            <span
              className={`truncate text-[10px] ${
                e.done ? 'text-[var(--text-subtle)] line-through' : 'text-[var(--text-muted)]'
              }`}
            >
              {e.title}
            </span>
          </div>
        ))}
        {others.length > 3 && (
          <div className="tabular pl-2.5 text-[10px] text-[var(--text-subtle)]">+{others.length - 3} more</div>
        )}
      </div>
    </button>
  )
}

function DayDetail({ day, onClose }: { day: MonthDay; onClose: () => void }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-3">
      <div className="flex items-baseline justify-between">
        <span className="tabular text-[12px] text-[var(--text)]">{day.date}</span>
        <button type="button" onClick={onClose} className="tap text-[12px] text-[var(--text-muted)]">
          Close
        </button>
      </div>

      {day.noClassReason && (
        <p className="tabular mt-1 text-[11px] text-[var(--text-muted)]">{day.noClassReason}</p>
      )}
      {day.milestone && <p className="mt-1 text-[12px] text-[var(--accent)]">{day.milestone}</p>}

      {day.labs.map((lab) => (
        <p key={lab.courseId} className="mt-2 text-[12px] text-[var(--text)]">
          <span className="tabular text-[var(--text-muted)]">{lab.start}</span> {lab.courseCode}
          {lab.title ? ` — ${lab.title}` : ''}
          {lab.room && <span className="tabular text-[var(--text-subtle)]"> · {lab.room}</span>}
        </p>
      ))}

      {day.entries.length === 0 && day.labs.length === 0 && (
        <p className="mt-2 text-[12px] text-[var(--text-subtle)]">Nothing due.</p>
      )}

      <ul className="mt-2 space-y-1">
        {day.entries.map((e) => (
          <li key={e.itemId} className="flex items-baseline gap-2">
            <span
              aria-hidden
              className="h-2 w-2 shrink-0 translate-y-[3px] rounded-full"
              style={{ backgroundColor: `var(--course-${e.colorIndex % 6})` }}
            />
            <span className="tabular w-16 shrink-0 text-[11px] text-[var(--text-muted)]">
              {e.timeDisplay || '—'}
            </span>
            <span className={`text-[12px] ${e.done ? 'line-through opacity-60' : 'text-[var(--text)]'}`}>
              {e.title}
              {e.gate && <span className="ml-1.5 text-[10px] uppercase text-[var(--overdue)]">gate</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Legend() {
  return (
    <div className="tabular flex flex-wrap gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.06em] text-[var(--text-subtle)]">
      <Swatch colour="var(--day-finals)">finals</Swatch>
      <Swatch colour="var(--day-study)">study</Swatch>
      <Swatch colour="var(--day-break)">break</Swatch>
      <span className="inline-flex items-center gap-1">
        <span className="h-2.5 w-4 rounded-[2px] bg-[var(--course-2)]" aria-hidden /> exam
      </span>
      <span className="inline-flex items-center gap-1">
        <span
          className="h-2.5 w-4 rounded-[2px] border"
          style={{ borderColor: 'var(--course-1)' }}
          aria-hidden
        />{' '}
        lab
      </span>
    </div>
  )
}

function Swatch({ colour, children }: { colour: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2.5 w-4 rounded-[2px]" style={{ backgroundColor: colour }} aria-hidden />
      {children}
    </span>
  )
}
